<script setup lang="ts">
  import { computed, type Component, type CSSProperties } from 'vue'
  import type { LayoutConfig, ModuleConfig } from '../utils/types'
  import SwitchGroup from './SwitchGroup.vue'
  import PointerSpeed from './PointerSpeed.vue'
  import LiftoffHeight from './LiftoffHeight.vue'
  import SensorAngle from './SensorAngle.vue'
  import WheelSpeed from './WheelSpeed.vue'
  import GlassTracking from './GlassTracking.vue'
  import KeyDebounce from './KeyDebounce.vue'
  import ReportRate from './ReportRate.vue'
  import { Skeleton } from '@rapoo/element'
  import type { ParametersContext } from '../../composables/useParameters'

  const props = defineProps<{
    layout: LayoutConfig
    parameters: ParametersContext
    /** 按当前布局的每个格子显示一块骨架 */
    placeholder?: boolean
  }>()

  /** 组件名 -> 组件实例映射 */
  const moduleComponents: Record<string, Component> = {
    SwitchGroup,
    PointerSpeed,
    LiftoffHeight,
    SensorAngle,
    WheelSpeed,
    GlassTracking,
    KeyDebounce,
    ReportRate,
  }

  /** 容器 grid 样式 */
  const gridStyle = computed<CSSProperties>(() => {
    const cols = props.layout.totalColumns || 24
    return {
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gridTemplateRows: props.layout.rowHeight,
      columnGap: props.layout.columnGap || '8px',
      rowGap: props.layout.rowGap || '8px',
    }
  })

  /** 计算单个模块的 grid 定位样式 */
  function getModuleStyle(mod: ModuleConfig): CSSProperties {
    return {
      gridColumn: `${mod.colStart} / span ${mod.colSpan}`,
      gridRow: `${mod.rowStart} / span ${mod.rowSpan || 1}`,
    }
  }
</script>

<template>
  <div :style="gridStyle">
    <div
      v-for="mod in layout.modules"
      :key="`${mod.component}-${mod.colStart}-${mod.rowStart}`"
      :style="getModuleStyle(mod)"
      class="min-w-0 min-h-0"
    >
      <div v-if="placeholder" class="glass-frosted flex h-full min-h-0 rounded-4xl p-4">
        <Skeleton class="h-full w-full rounded-2xl" animation-type="shimmer" />
      </div>
      <component
        :is="moduleComponents[mod.component]"
        v-else
        :size="mod.size"
        v-bind="mod.component === 'ReportRate' ? {} : { parameters: props.parameters }"
        class="h-full"
      />
    </div>
  </div>
</template>
